import { useEffect, useMemo, useState } from 'react';

const NOTICE_CONTENT = {
  terms: {
    title: '이용약관',
    body: [
      'DoroParty는 유니온 레이드 운영을 돕기 위한 비공식 팬 도구입니다.',
      '사용자는 자신의 계정과 유니온 데이터에 대한 접근 권한을 올바르게 사용해야 하며, 서비스 운영을 방해하거나 다른 이용자의 데이터를 침해해서는 안 됩니다.',
      '서비스 기능과 제공 범위는 운영 사정에 따라 변경될 수 있으며, 게임사 정책 또는 외부 서비스 변경으로 일부 기능이 중단될 수 있습니다.',
    ],
  },
  privacy: {
    title: '개인정보처리방침',
    body: [
      'DoroParty의 로그인과 운영을 위해 이메일 계정, 인증 식별자, 게임 닉네임, 공개 프로필 식별자, 유니온 소속 정보가 처리될 수 있습니다.',
      '사용자가 요청한 경우 보유 니케 정보, 레이드 기록, 모의전 기록, 추천덱과 운영 데이터가 서비스 제공 목적으로 저장될 수 있습니다.',
      '브라우저에는 BlablaLink 로그인 쿠키나 서버용 인증 정보를 노출하지 않으며, 계정 삭제 기능을 통해 사이트 계정과 연결 데이터 삭제를 요청할 수 있습니다.',
    ],
  },
  fan: {
    title: '팬사이트 안내',
    body: [
      'DoroParty는 『승리의 여신: 니케』의 비공식 팬 운영 도구이며 개발사, 퍼블리셔 및 관련 권리자와 제휴·후원·공식 관계가 없습니다.',
      '게임명, 캐릭터, 이미지, 로고, 상표와 기타 게임 관련 자료의 권리는 각각의 권리자에게 있습니다.',
      '권리자의 요청이나 운영 정책 변경이 있을 경우 관련 이미지와 자료는 수정 또는 제거될 수 있습니다.',
    ],
  },
  contact: {
    title: '사이트 문의',
    body: [
      '로그인 후 사이트 하단의 문의 버튼에서 DoroParty 이용·계정·오류·데이터 관련 문의를 등록할 수 있습니다.',
      '등록한 문의는 본인과 DoroParty 플랫폼 관리자만 확인할 수 있습니다.',
    ],
  },
  licenses: {
    title: '오픈소스 라이선스',
    body: [
      'DoroParty는 React, Vite, Supabase JavaScript Client 등 오픈소스 소프트웨어를 사용합니다.',
      '각 오픈소스 패키지의 저작권과 라이선스는 해당 프로젝트와 권리자에게 있습니다.',
      '배포 빌드에 포함된 패키지의 상세 라이선스는 프로젝트 저장소와 패키지 잠금 파일을 기준으로 확인할 수 있습니다.',
    ],
  },
};

function FooterDialog({ noticeKey, supportEmail, onClose }) {
  const notice = NOTICE_CONTENT[noticeKey];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!notice) return null;

  return (
    <div className="site-footer-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="site-footer-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-footer-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p>DoroParty POLICY</p>
            <h2 id="site-footer-dialog-title">{notice.title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="안내 닫기">닫기</button>
        </header>
        <div className="site-footer-dialog-body">
          {notice.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {noticeKey === 'contact' ? (
            supportEmail
              ? <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
              : <p className="site-footer-contact-pending">로그인 후 사이트 문의 기능을 이용해주세요.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function SiteFooter({ compact = false }) {
  const [noticeKey, setNoticeKey] = useState('');
  const currentYear = new Date().getFullYear();
  const supportEmail = useMemo(() => String(import.meta.env.VITE_SUPPORT_EMAIL || '').trim(), []);
  const closeDialog = () => setNoticeKey('');
  const openNotice = (key) => {
    const signedInSelector = '.desktop-shell,.mobile-shell,.desktop-entry-shell,.mobile-entry-shell,.site-support-standalone';
    const inSignedInApp = typeof document !== 'undefined' && Boolean(document.querySelector(signedInSelector));
    if (key === 'contact' && inSignedInApp) {
      closeDialog();
      window.location.hash = '/support';
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }
    setNoticeKey(key);
  };

  return (
    <>
      <footer className={`site-footer ${compact ? 'compact' : ''}`.trim()}>
        <p className="site-footer-copyright">Copyright © 2026{currentYear > 2026 ? `-${currentYear}` : ''} DoroParty. All Rights Reserved.</p>
        <p>DoroParty는 『승리의 여신: 니케』의 비공식 팬 운영 도구이며 개발사·퍼블리셔 및 관련 권리자와 공식 관계가 없습니다.</p>
        <p>게임명, 캐릭터, 이미지, 로고와 관련 저작권·상표권은 각각의 권리자에게 있습니다.</p>
        <nav aria-label="사이트 정책 및 안내">
          <button type="button" onClick={() => openNotice('terms')}>이용약관</button>
          <button type="button" onClick={() => openNotice('privacy')}>개인정보처리방침</button>
          <button type="button" onClick={() => openNotice('fan')}>팬사이트 안내</button>
          <button type="button" onClick={() => openNotice('contact')}>문의</button>
          <button type="button" onClick={() => openNotice('licenses')}>오픈소스 라이선스</button>
        </nav>
      </footer>
      {noticeKey ? <FooterDialog noticeKey={noticeKey} supportEmail={supportEmail} onClose={closeDialog} /> : null}
    </>
  );
}
